const backendApiUrl: string = process.env.REACT_APP_BACKEND_API_URL!

export type RepairPartsQuery = {
	productCatalogueId?: string
	componentTypeId?: string
	materialId?: string
	category?: string | string[]
	search?: string
	limit?: number
	testMode?: boolean
}

const normalizePartsResponse = (payload: unknown): any[] => {
	if (Array.isArray(payload)) {
		return payload
	}
	if (payload && typeof payload === 'object') {
		const record = payload as Record<string, unknown>
		if (Array.isArray(record.data)) {
			return record.data
		}
		if (Array.isArray(record.items)) {
			return record.items
		}
		if (record.data && typeof record.data === 'object') {
			const data = record.data as Record<string, unknown>
			if (Array.isArray(data.items)) {
				return data.items
			}
			if (Array.isArray(data.results)) {
				return data.results
			}
		}
		if (Array.isArray(record.results)) {
			return record.results
		}
	}
	return []
}

export const fetchRepairParts = async (
	params: RepairPartsQuery = {},
	signal?: AbortSignal,
	region?: string
): Promise<any[]> => {
	const baseUrl = backendApiUrl.replace(/\/$/, '')
	const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`
	const url = new URL(`${apiBase}/shop/parts/`)
	if (params.productCatalogueId) {
		url.searchParams.set('productCatalogueId', params.productCatalogueId)
	}
	if (params.componentTypeId) {
		url.searchParams.set('componentTypeId', params.componentTypeId)
	}
	if (params.materialId) {
		url.searchParams.set('materialId', params.materialId)
	}
	if (params.category) {
		const categoryValue = Array.isArray(params.category) ? params.category.join(',') : params.category
		if (categoryValue.trim()) {
			url.searchParams.set('category', categoryValue)
		}
	}
	if (params.search) {
		url.searchParams.set('search', params.search)
	}
	if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
		const normalizedLimit = Math.min(200, Math.max(1, Math.floor(params.limit)))
		url.searchParams.set('limit', String(normalizedLimit))
	}
	if (typeof params.testMode === 'boolean') {
		url.searchParams.set('testMode', String(params.testMode))
	}

	const response = await fetch(url.toString(), {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			...(region ? { 'x-country-code': region.toUpperCase() } : {}),
		},
		signal,
	})

	if (!response.ok) {
		throw new Error(`Failed to fetch repair parts (${response.status})`)
	}

	const payload = await response.json()
	return normalizePartsResponse(payload)
}

export const fetchRepairPartDetail = async (
	partId: string,
	signal?: AbortSignal,
	region?: string
): Promise<any | null> => {
	const baseUrl = backendApiUrl.replace(/\/$/, '')
	const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`
	const url = new URL(`${apiBase}/shop/products/${partId}/`)

	const response = await fetch(url.toString(), {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			...(region ? { 'x-country-code': region.toUpperCase() } : {}),
		},
		signal,
	})

	if (!response.ok) {
		throw new Error(`Failed to fetch repair part (${response.status})`)
	}

	return response.json()
}
